// @bunker/shared/api — the runtime-agnostic Bunker REST client.
//
// The web app and the React Native app share this one implementation. They
// differ only in how they configure it (see {@link ApiClientConfig}):
//   * web    — `createApi({ withCredentials: true })` (cookie auth, same-origin)
//   * native — `createApi({ baseUrl, getToken })` (bearer token, absolute origin)
//
// The composed object's method names are flat and identical to the historical
// web `api` facade, so consumers import `api.sendMessage(...)` exactly as before.

import { createRequest, defaultGenerateNonce, type ApiClientConfig } from './http.js';
import { makeAuthApi } from './domains/authApi.js';
import { makeMeApi } from './domains/meApi.js';
import { makeGuildsApi } from './domains/guildsApi.js';
import { makeChannelsApi } from './domains/channelsApi.js';
import { makeMessagesApi } from './domains/messagesApi.js';
import { makeDmsApi } from './domains/dmsApi.js';
import { makeMediaApi } from './domains/mediaApi.js';
import { makeUsersApi } from './domains/usersApi.js';
import { makeFollowUpsApi } from './domains/followUpsApi.js';
import { makeLinkPreviewApi } from './domains/linkPreviewApi.js';
import { makeVaultShelvesApi } from './domains/vaultShelvesApi.js';
import { makeReportsApi } from './domains/reportsApi.js';
import { makeBugReportsApi } from './domains/bugReportsApi.js';

/**
 * Build the Bunker API client. Composes the per-domain modules over a single
 * configured `request` function. Returns one flat object whose method names
 * match the historical web facade.
 */
export function createApi(config: ApiClientConfig = {}) {
  const request = createRequest(config);
  const generateNonce = config.generateNonce ?? defaultGenerateNonce;

  return {
    ...makeAuthApi(request),
    ...makeMeApi(request),
    ...makeGuildsApi(request),
    ...makeChannelsApi(request),
    ...makeMessagesApi(request, generateNonce),
    ...makeDmsApi(request),
    ...makeMediaApi(request),
    ...makeUsersApi(request),
    ...makeFollowUpsApi(request),
    ...makeLinkPreviewApi(request),
    ...makeVaultShelvesApi(request),
    ...makeReportsApi(request),
    ...makeBugReportsApi(request),
  };
}

/** The fully-composed API client type (what `createApi` returns). */
export type Api = ReturnType<typeof createApi>;

export {
  createRequest,
  defaultGenerateNonce,
  defaultGenerateNonce as generateClientNonce,
  buildQuery,
} from './http.js';
export type {
  ApiClientConfig,
  RequestFn,
  RequestError,
  FetchLike,
  FetchResponse,
  FetchRequestInit,
} from './http.js';
export type { GlobalSearchHit } from './types.js';
